import { canvasDocumentRuntime } from './canvasDocumentRuntime';

let activeSlot: HTMLElement | null = null;
/** Conversation the current slot is prepared to receive, even before its scope exists. */
let activeSlotConversationId: string | null = null;
let residentConversationId: string | null = null;
/**
 * Réveillés après chaque réclamation d'emplacement (#1588). Seul
 * `claimCanvasHost` sort un hôte du parking : c'est donc le seul endroit où une
 * attente d'affichage peut se dénouer.
 */
const abonnesAuxReclamations = new Set<() => void>();
const abonnesAuxSlotsPrepares = new Set<() => void>();

function resolveScope(conversationId: string) {
  return canvasDocumentRuntime.getScope(conversationId);
}

function placeExclusiveHost(scope: NonNullable<ReturnType<typeof resolveScope>>, slot: HTMLElement): void {
  if (residentConversationId && residentConversationId !== scope.conversationId) {
    canvasDocumentRuntime.getScope(residentConversationId)?.releaseHost(slot);
  }
  scope.claimHost(slot);
  residentConversationId = scope.conversationId;
}

/**
 * Façade d'emplacement au-dessus des hosts possédés par chaque scope documentaire.
 *
 * `activeSlot` ne désigne qu'un emplacement d'écran ; il ne possède aucun DOM
 * SuperDoc. La destination réelle est toujours résolue par la CONVERSATION
 * (#1336), puis déplacée par le scope qui la possède.
 */
export function getCanvasHostElement(conversationId: string): HTMLDivElement {
  const scope = resolveScope(conversationId);
  if (!scope) {
    throw new Error(
      `Canvas host unavailable for conversationId=${conversationId}: scope=missing. `
      + 'Acquire the canvas document of this conversation on the first opening gesture '
      + 'before creating its portal.',
    );
  }
  return scope.getDomResources().host;
}

export function isCanvasHostParked(conversationId: string): boolean {
  const scope = resolveScope(conversationId);
  return scope ? scope.isHostParked() : true;
}

export function claimCanvasHost(slot: HTMLElement, conversationId: string): void {
  const previousSlot = activeSlot;
  const scope = resolveScope(conversationId);
  if (
    previousSlot
    && previousSlot !== slot
    && residentConversationId
    && residentConversationId !== scope?.conversationId
  ) {
    canvasDocumentRuntime.getScope(residentConversationId)?.releaseHost(previousSlot);
  }
  activeSlot = slot;
  activeSlotConversationId = conversationId;
  if (scope) {
    placeExclusiveHost(scope, slot);
  } else if (residentConversationId !== null) {
    // Un slot en transition doit montrer son chargeur, pas le document sortant.
    // Le nouveau host sera posé par l'abonnement `CanvasSlot` dès que son scope
    // est créé ; entre-temps le précédent retourne au parking.
    canvasDocumentRuntime.getScope(residentConversationId)?.releaseHost(slot);
    residentConversationId = null;
  }
  // Copie : un abonné qui se dénoue se retire pendant le parcours.
  for (const reveiller of [...abonnesAuxReclamations]) reveiller();
  for (const reveiller of [...abonnesAuxSlotsPrepares]) reveiller();
}

/**
 * Attend qu'un slot assistant existe pour CE fil, sans exiger encore un scope.
 *
 * Le chargement DOCX froid utilise cette barrière avant `requestResident` : le
 * slot peut donc précéder le host, qui sera déplacé à sa création, plutôt que
 * l'inverse qui initialise SuperDoc dans le parking.
 */
export function attendreQueLeSlotCanvasSoitPret(conversationId: string, delaiMs: number): Promise<boolean> {
  const slotEstPret = () => activeSlot !== null && activeSlotConversationId === conversationId;
  if (slotEstPret()) return Promise.resolve(true);
  return new Promise((resoudre) => {
    let echeance: ReturnType<typeof setTimeout> | null = null;
    const conclure = (pret: boolean): void => {
      abonnesAuxSlotsPrepares.delete(verifier);
      if (echeance !== null) clearTimeout(echeance);
      resoudre(pret);
    };
    function verifier(): void {
      if (slotEstPret()) conclure(true);
    }
    abonnesAuxSlotsPrepares.add(verifier);
    echeance = setTimeout(() => conclure(false), delaiMs);
  });
}

/**
 * Attend que l'hôte du document de ce fil quitte le parking, au plus `delaiMs`.
 *
 * Pourquoi (#1588) : quand Hector écrit depuis le dock « Demander à Hector »,
 * le dock bascule vers `/assistant?c=<fil>` au moment même où l'action part.
 * La navigation est une transition React (route chargée à la demande), et
 * l'emplacement de l'assistant ne réclame l'hôte qu'au commit de cette route —
 * alors que l'exécuteur agent n'enchaîne que des microtâches. Une écriture qui
 * juge ce que l'avocat VOIT (`write_markdown` en `replace`, #1586) lirait donc
 * « parqué » sur un document qui arrive à l'écran, et se refuserait à tort.
 *
 * Bornée : un écran qui ne vient pas (avocat parti ailleurs, canvas masqué) ne
 * doit pas retenir l'action jusqu'au délai du demandeur backend.
 *
 * @returns `true` dès que l'hôte est dans un emplacement (tout de suite s'il y
 * est déjà), `false` à l'échéance.
 */
export function attendreQueLHoteSoitAffiche(conversationId: string, delaiMs: number): Promise<boolean> {
  if (!isCanvasHostParked(conversationId)) return Promise.resolve(true);
  return new Promise((resoudre) => {
    let echeance: ReturnType<typeof setTimeout> | null = null;
    const conclure = (affiche: boolean): void => {
      abonnesAuxReclamations.delete(verifier);
      if (echeance !== null) clearTimeout(echeance);
      resoudre(affiche);
    };
    // Une réclamation pour un AUTRE fil réveille aussi : on relit l'hôte de
    // celui-ci plutôt que de se fier à la conversation réclamée.
    function verifier(): void {
      if (!isCanvasHostParked(conversationId)) conclure(true);
    }
    abonnesAuxReclamations.add(verifier);
    echeance = setTimeout(() => conclure(false), delaiMs);
  });
}

export function releaseCanvasHost(slot: HTMLElement, conversationId: string): void {
  if (activeSlot !== slot) return;
  const targetId = conversationId;
  if (targetId !== activeSlotConversationId) return;
  if (targetId === residentConversationId) {
    canvasDocumentRuntime.getScope(targetId)?.releaseHost(slot);
    residentConversationId = null;
  }
  activeSlot = null;
  activeSlotConversationId = null;
  for (const reveiller of [...abonnesAuxSlotsPrepares]) reveiller();
}

/** @internal Test isolation for the module-level slot projection. */
export function resetCanvasHostSlotForTests(): void {
  activeSlot = null;
  activeSlotConversationId = null;
  residentConversationId = null;
  abonnesAuxReclamations.clear();
  abonnesAuxSlotsPrepares.clear();
}
